/**
 * Shared widget tour steps — одна форма шагов, но два ключа для двух
 * страниц. Новый пользователь должен увидеть обучалку И на `/generate`,
 * И на `/bots/*` — флаги независимые.
 *
 * Билдер принимает срез `useGenerate()` + флаги режима и строит массив
 * TourStep'ов. Шаги динамические — их содержимое зависит от того,
 * требуется ли модели image/video, есть ли уже uploaded данные и т.д.
 *
 * Помимо auto-показа при первом визите, тур можно перезапустить вручную
 * через help-кнопку `?` в `ModelBadge` (см. Generate.tsx / GenerateWidget.tsx).
 */
import React from 'react'
import type { TourStep } from '../components/SpotlightTour'

/** Подмножество `useGenerate()`, которое нужно туру. */
export interface WidgetTourGenState {
	prompt: string
	uploadedImage: { url?: string } | null
	referenceImages: { url?: string }[]
	uploadedVideo: { url?: string } | null
	isLoading: boolean
	result: string | null
	requiresImage: boolean
	requiresTwoImages: boolean
	requiresVideo: boolean
	supportsMultipleImages: boolean
	/** true когда текущая модель — аудио (Suno и т.д.) */
	isAudio: boolean
	/** true когда текущая модель — презентация (Umnik Slides). */
	isPresentation?: boolean
	/** Модель не читает текст (face-swap) — шаг про промпт пропускаем. */
	noPrompt?: boolean
}

// ── Image / Video steps ──────────────────────────────────────

function buildImageVideoSteps(gen: WidgetTourGenState): TourStep[] {
	const imageRequired = gen.requiresImage || gen.requiresTwoImages

	const steps: TourStep[] = [
		{
			// UploadThumb → data-slot-type="image" | MultiImgRow → .gm-multi-row
			// mobileTargetSelector включает ОБА варианта: .gm__mobile-uploads
			// (для /generate) и .gm__uploads (виджет форсирует его visible на
			// мобилке). querySelector вернёт первый найденный в DOM.
			targetSelector: '.gm__uploads [data-slot-type="image"], .gm__uploads .gm-multi-row',
			mobileTargetSelector:
				'.gm__mobile-uploads [data-slot-type="image"], .gm__uploads [data-slot-type="image"], .gm__mobile-uploads .gm-multi-row, .gm__uploads .gm-multi-row',
			title: imageRequired ? 'Загрузите фото' : 'Референсное фото',
			description: imageRequired ? (
				<ul className="spt__list">
					<li>Нажмите на слот и выберите фото</li>
					<li>Изображение станет основой для генерации</li>
				</ul>
			) : (
				<ul className="spt__list">
					<li>Добавьте фото чтобы задать стиль или аватара</li>
					<li>Без фото нейросеть создаст образ сама по промпту</li>
					<li>
						Поле <strong>необязательное</strong>
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
			completed: !!gen.uploadedImage?.url || (gen.referenceImages?.length ?? 0) > 0,
		},
	]

	if (gen.requiresVideo) {
		steps.push({
			targetSelector: '.gm__uploads [data-slot-type="video"]',
			mobileTargetSelector: '.gm__mobile-uploads [data-slot-type="video"], .gm__uploads [data-slot-type="video"]',
			title: 'Загрузите видео',
			description: (
				<ul className="spt__list">
					<li>Нажмите на слот и выберите видео</li>
					<li>Видео задаёт движение — нейросеть перенесёт его на фото</li>
				</ul>
			),
			nextLabel: 'Дальше →',
			completed: !!gen.uploadedVideo?.url,
		})
	}

	// Модель текст не читает — поле промпта скрыто, а на его месте объяснение.
	// Шаг «Напишите промпт» указывал бы на надпись «текст не нужен».
	if (!gen.noPrompt)
	steps.push(
		{
			targetSelector: '.gm__prompt',
			title: 'Напишите промпт',
			description:
				(gen.prompt ?? '').trim().length > 0 ? (
					<ul className="spt__list">
						<li>Промпт уже заполнен</li>
						<li>Вы можете его отредактировать или полностью заменить своим</li>
						<li>Чем подробнее описание — тем точнее результат</li>
					</ul>
				) : (
					<ul className="spt__list">
						<li>Опишите что хотите получить</li>
						<li>Чем подробнее — тем точнее результат</li>
					</ul>
				),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.gm__pills',
			mobileTargetSelector: '.gm__mobile-model-btn, .gm__pills',
			title: 'Настройте параметры',
			description:
				'Здесь собраны все настройки генерации — выберите нужные или оставьте по умолчанию.',
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.gm__bottom .gm__send',
			mobileTargetSelector: '.gm__mobile-bar .gm__send, .gm__bottom .gm__send',
			title: 'Нажмите Создать',
			description: (
				<ul className="spt__list">
					<li>Результат появится через 10–60 секунд</li>
					<li>Можно скачать или пересоздать с изменениями</li>
				</ul>
			),
			nextLabel: 'Понятно →',
			completed: gen.isLoading || !!gen.result,
		},
	)

	return steps
}

// ── Audio steps (Suno и аналоги) ─────────────────────────────

function buildAudioSteps(gen: WidgetTourGenState): TourStep[] {
	return [
		{
			// Поле «Название трека» — .gm__field-outlined внутри .gm__uploads--audio
			targetSelector: '.gm__uploads--audio .gm__field-outlined',
			mobileTargetSelector: '.gm__uploads--audio .gm__field-outlined',
			title: 'Название трека',
			description: (
				<ul className="spt__list">
					<li>Укажите название для вашего трека</li>
					<li>
						Поле <strong>необязательное</strong> — но с ним удобнее находить треки в истории
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.gm__prompt',
			title: 'Опишите музыку или лирику',
			description:
				(gen.prompt ?? '').trim().length > 0 ? (
					<ul className="spt__list">
						<li>Текст уже заполнен — вы можете его отредактировать</li>
						<li>
							В <strong>кастомном режиме</strong> это лирика песни (текст куплетов и припевов)
						</li>
						<li>В обычном режиме — просто описание стиля и настроения</li>
					</ul>
				) : (
					<ul className="spt__list">
						<li>Опишите стиль, настроение и тему трека</li>
						<li>
							В <strong>кастомном режиме</strong> можно вводить лирику напрямую
						</li>
					</ul>
				),
			nextLabel: 'Дальше →',
		},
		{
			// Кнопка «Сгенерировать текст песни» — AI-помощник который
			// сам пишет lyrics по теме. Результат подставляется в prompt.
			targetSelector: '.suno-lyrics-btn',
			title: 'Или пусть ИИ напишет текст сам',
			description: (
				<ul className="spt__list">
					<li>Нажмите — укажите тему, и ИИ сочинит текст песни</li>
					<li>
						Готовая лирика подставится в промпт в <strong>кастомном режиме</strong>
					</li>
					<li>Удобно когда нужна песня на свои слова, но не знаете с чего начать</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			// Аудио-пилюли: стиль, вокал, инструментал, кастомный
			targetSelector: '.gm__pills',
			mobileTargetSelector: '.gm__mobile-model-btn, .gm__pills',
			title: 'Настройки аудио',
			description: (
				<ul className="spt__list">
					<li>
						<strong>Стиль</strong> — жанр музыки (Pop, Rock, Jazz...)
					</li>
					<li>
						<strong>Вокал</strong> — мужской, женский или авто
					</li>
					<li>
						<strong>Инструментал</strong> — музыка без слов
					</li>
					<li>
						<strong>Кастомный</strong> — ручной ввод лирики в промпте
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.gm__bottom .gm__send',
			mobileTargetSelector: '.gm__mobile-bar .gm__send, .gm__bottom .gm__send',
			title: 'Нажмите Создать',
			description: (
				<ul className="spt__list">
					<li>Генерация трека занимает 30–90 секунд</li>
					<li>Вы получите один или несколько вариантов — можно прослушать и выбрать лучший</li>
				</ul>
			),
			nextLabel: 'Понятно →',
			completed: gen.isLoading || !!gen.result,
		},
	]
}

// ── Public builder ───────────────────────────────────────────

export function buildWidgetTourSteps(gen: WidgetTourGenState): TourStep[] {
	if (gen.isPresentation) return buildPresentationSteps(gen)
	return gen.isAudio ? buildAudioSteps(gen) : buildImageVideoSteps(gen)
}

// ── Presentation steps (Gamma, режим «Создать с нуля») ────────────────────────

function buildPresentationSteps(gen: WidgetTourGenState): TourStep[] {
	return [
		{
			// Режим один — «Создать с нуля»: тумблера и дропзоны PPTX больше нет
			// (модель редактирования скрыта в админке, 10.09).
			targetSelector: '.gm__pills',
			mobileTargetSelector: '.gm__mobile-model-btn, .gm__pills',
			title: 'Настрой презентацию',
			description: (
				<ul className="spt__list">
					<li>Число слайдов, язык, откуда картинки, формат и объём текста</li>
					<li>Тему оформления выбери там же — в пилюлях под промптом</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.gm__prompt',
			title: 'Опиши что нужно',
			description: (
				<ul className="spt__list">
					<li>Тема презентации: <strong>«Бизнес-план кофейни»</strong>, «История ИИ»</li>
					<li>Можно задать структуру: какие разделы и что подчеркнуть</li>
					<li>Чем конкретнее — тем точнее результат</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.gm__bottom .gm__send',
			mobileTargetSelector: '.gm__mobile-bar .gm__send, .gm__bottom .gm__send',
			title: 'Нажми Создать',
			description: (
				<ul className="spt__list">
					<li>Генерация занимает 1–2 минуты</li>
					<li>Готовый PPTX появится в слоте справа — можно скачать</li>
					<li>Цена видна на кнопке — зависит от объёма текста и картинок</li>
				</ul>
			),
			nextLabel: 'Понятно →',
			completed: gen.isLoading || !!gen.result,
		},
	]
}

// ── Styles gallery tour (для /bots/images/styles и /bots/video/styles) ──

/**
 * Шаги тура по галерее стилей на /bots/images/styles и /bots/video/styles.
 * Объясняет: что такое стили, как применить, как создать видео.
 *
 * `category` определяет, показывать ли шаг «Создать видео»
 * (актуально только для image, не для video — у video нет i2v-кнопки на стиле).
 */
export function buildStylesTourSteps(category: 'image' | 'video'): TourStep[] {
	const steps: TourStep[] = [
		{
			targetSelector: '.bots-styles .card-gallery',
			title: 'Блок стилей',
			description: (
				<ul className="spt__list">
					<li>Готовые визуальные стили от наших дизайнеров</li>
					<li>Каждый стиль автоматически подставляет промпт и настройки</li>
					<li>Можно применить к новой генерации или к уже готовой</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.bots-styles .gen-card:first-child .gen-card__quick-action-btn',
			title: 'Применить стиль',
			description: (
				<ul className="spt__list">
					<li>Нажмите чтобы применить стиль к текущей генерации</li>
					<li>Промпт и настройки подставятся автоматически</li>
				</ul>
			),
			nextLabel: category === 'image' ? 'Дальше →' : 'Понятно →',
		},
	]

	// Кнопка «Создать видео» есть только на image-стилях
	if (category === 'image') {
		steps.push({
			targetSelector: '.bots-styles .gen-card:first-child .gen-card__create-video-btn',
			title: 'Создать видео',
			description: (
				<ul className="spt__list">
					<li>Превращает готовое изображение в видео</li>
					<li>Нейросеть анимирует сцену сохраняя стиль и аватаров</li>
				</ul>
			),
			nextLabel: 'Понятно →',
		})
	}

	return steps
}

/**
 * Storage key для тура на странице `/generate`. Исторический ключ —
 * существующие пользователи, прошедшие тур, не увидят его снова.
 */
export const GENERATE_TOUR_KEY = 'umnik_widget_tour_done'

/**
 * Storage key для тура внутри виджета на `/bots/*`. Независим от
 * GENERATE_TOUR_KEY — новый пользователь увидит обучалку на обеих
 * страницах по разу (это обучение разным рабочим контекстам).
 */
export const BOTS_WIDGET_TOUR_KEY = 'umnik_bots_widget_tour_done'

/**
 * Storage key для тура галереи стилей на `/bots/images/styles`
 * и `/bots/video/styles`. Объясняет кнопки на карточках.
 */
export const BOTS_STYLES_TOUR_KEY = 'umnik_bots_styles_tour_done'

// ── Suno Style Sheet tour (для SunoStyleSheet modal) ─────────────

/**
 * Шаги тура для модала «Настроить стиль песни» (SunoStyleSheet).
 * Показывается при первом открытии + перезапускается через `?` help-кнопку
 * в header'е.
 *
 * Цель — объяснить что:
 *   1. Можно добавлять жанры-теги через pills или свободным текстом
 *   2. Boost-AI улучшает стиль (детализирует/развивает)
 *   3. «Исключить» — чтобы убрать нежелательные характеристики
 *   4. Изменения применяются только по «Применить»
 */
export function buildStyleSheetTourSteps(): TourStep[] {
	return [
		{
			// Блок «Стиль» — обёрнут в .suno-style-sheet__style-block в
			// SunoStyleEditor. Без :has() — тот селектор не всегда срабатывал.
			targetSelector: '.suno-style-sheet__style-block',
			title: 'Задайте стиль песни',
			description: (
				<ul className="spt__list">
					<li>
						Кликайте на <strong>популярные жанры</strong> — они добавятся как pill'ы
					</li>
					<li>Или пишите свободный текст прямо в поле («dark mysterious vibes»)</li>
					<li>Можно комбинировать: несколько жанров + своё описание звучания</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			// Кнопка «Улучшить через AI» (boost-style)
			targetSelector: '.suno-style-sheet__boost-btn',
			title: 'Улучшить стиль через AI',
			description: (
				<ul className="spt__list">
					<li>AI раскроет ваш стиль — детализирует настроение, инструменты, эпоху</li>
					<li>
						Стоит несколько токенов (видно на кнопке) — но результат часто лучше
					</li>
					<li>Если не понравилось — можно вернуть исходный текст</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			// Блок «Исключить» — обёрнут в .suno-style-sheet__negative-block.
			targetSelector: '.suno-style-sheet__negative-block',
			title: 'Исключите то что не хотите',
			description: (
				<ul className="spt__list">
					<li>Указывайте жанры/характеристики которых НЕ должно быть</li>
					<li>Например «Screaming, Loud drums» — Suno их избежит в треке</li>
					<li>
						Поле <strong>необязательное</strong> — если не нужно, оставьте пустым
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			// Кнопка «Применить» в footer'е
			targetSelector: '.suno-style-sheet__actions .suno-sheet-btn--primary',
			title: 'Примените изменения',
			description: (
				<ul className="spt__list">
					<li>
						Стиль применится только после <strong>«Применить»</strong>
					</li>
					<li>«Отмена» закрывает модал без изменений</li>
					<li>«Очистить» — сбрасывает оба поля в локальном буфере</li>
				</ul>
			),
			nextLabel: 'Понятно →',
		},
	]
}

/**
 * Storage key для тура «Настроить стиль песни».
 */
export const SUNO_STYLE_SHEET_TOUR_KEY = 'umnik_suno_style_sheet_tour_done'

// ── Suno Lyrics Sheet tour (для «Сгенерировать текст песни») ──────

/**
 * Шаги тура для sheet'а «Сгенерировать текст песни» (SunoLyricsButton).
 * Показывается при первом открытии + перезапускается через `?` help-кнопку.
 *
 * Цель — объяснить:
 *   1. Структуру из двух шагов (Тема → Результат)
 *   2. Название — optional, для связи с историей
 *   3. Тема — основное поле, что именно писать
 *   4. История сохранённых текстов (если есть)
 *   5. Кнопка «Сгенерировать» — запускает процесс
 */
export function buildLyricsSheetTourSteps(hasHistory: boolean): TourStep[] {
	const steps: TourStep[] = [
		{
			// Поле «Название песни» — optional, нужно для связи с историей
			targetSelector: '#suno-title',
			title: 'Название (необязательно)',
			description: (
				<ul className="spt__list">
					<li>
						Указывать <strong>не обязательно</strong>, но с названием
						удобнее находить свои тексты в истории
					</li>
					<li>Если оставите пустым — сохранится как «Без названия»</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			// Поле «О чём песня?» — основной prompt
			targetSelector: '#suno-topic',
			title: 'О чём песня?',
			description: (
				<ul className="spt__list">
					<li>Опишите тему, настроение, образы — чем конкретнее, тем лучше</li>
					<li>
						Примеры: <em>«песня о море и свободе, настроение меланхоличное»</em>,
						<em> «весёлый трек про дружбу и молодость»</em>
					</li>
					<li>AI сам напишет текст по вашему описанию — с куплетами и припевом</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
	]

	if (hasHistory) {
		steps.push({
			// Кнопка «Мои сохранённые тексты» — открывает историю
			targetSelector: '.suno-lyrics-sheet__history-link',
			title: 'История сохранённых текстов',
			description: (
				<ul className="spt__list">
					<li>Все тексты которые вы генерировали здесь — под рукой</li>
					<li>
						Можно выбрать старый вариант вместо новой генерации — это <strong>бесплатно</strong>
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		})
	}

	steps.push({
		// Кнопка «Сгенерировать»
		targetSelector: '.suno-lyrics-sheet__actions .suno-sheet-btn--primary',
		title: 'Сгенерировать текст',
		description: (
			<ul className="spt__list">
				<li>Генерация занимает обычно 10–60 секунд</li>
				<li>Получите <strong>несколько вариантов</strong> — выберите лучший</li>
				<li>Выбранный текст подставится в промпт виджета генерации</li>
			</ul>
		),
		nextLabel: 'Понятно →',
	})

	return steps
}

/**
 * Storage key для тура «Сгенерировать текст песни».
 */
export const SUNO_LYRICS_SHEET_TOUR_KEY = 'umnik_suno_lyrics_sheet_tour_done'

// ── Suno Actions Sheet tour (для «Работать с треком») ───────────

/**
 * Шаги тура для SunoActionsSheet — popover/sheet с операциями над треком.
 * Показывается при первом открытии + перезапускается через `?` help-кнопку.
 *
 * 4 шага — по одному на каждую группу:
 *   1. Быстро        — one-click операции (WAV/Стемы/MIDI/…)
 *   2. Новая версия  — параметрические операции через виджет (Продлить/…)
 *   3. Редактирование — спец UI (Replace/Mashup/Custom Cover)
 *   4. Опасная зона  — удаление трека
 */
export function buildActionsSheetTourSteps(): TourStep[] {
	return [
		{
			targetSelector: '.suno-actions-sheet__group--folders',
			title: 'Папки — организация трека',
			description: (
				<ul className="spt__list">
					<li>
						<strong>Добавить в папку</strong> — положить трек в одну или несколько папок
					</li>
					<li>Один трек может быть сразу в нескольких папках — они виртуальные</li>
					<li>
						На странице папки появляется ещё <strong>«Убрать из X»</strong>
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-actions-sheet__group--quick',
			title: 'Быстро — один клик → готово',
			description: (
				<ul className="spt__list">
					<li>
						<strong>WAV</strong> — lossless-файл для архива
					</li>
					<li>
						<strong>Стемы</strong> — отделить вокал от инструментала
					</li>
					<li>
						<strong>MIDI</strong> — ноты трека (для DAW)
					</li>
					<li>
						<strong>Обложка</strong> — AI-генерация или загрузка своей картинки
					</li>
					<li>
						<strong>Видеоклип / Lyrics+ / Персона</strong> — дополнительные материалы
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-actions-sheet__group--parametric',
			title: 'Новая версия — откроет виджет',
			description: (
				<ul className="spt__list">
					<li>
						<strong>Продлить</strong> — добавить продолжение к треку
					</li>
					<li>
						<strong>Cover</strong> — перепоёт в другом жанре
					</li>
					<li>
						<strong>+ Вокал</strong> / <strong>+ Инструментал</strong> — добавить часть к существующей
					</li>
					<li>
						Клик → в виджете появится <strong>pin</strong> с этим треком. Настроишь параметры — нажмёшь «Создать»
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-actions-sheet__group--editing',
			title: 'Редактирование — спец-инструменты',
			description: (
				<ul className="spt__list">
					<li>
						<strong>Заменить фрагмент</strong> — выдели часть трека на waveform и перегенерируй её
					</li>
					<li>
						<strong>Mashup</strong> — смешай с другим треком из библиотеки
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-actions-sheet__group--danger',
			title: 'Удалить трек',
			description: (
				<ul className="spt__list">
					<li>Убирает трек из библиотеки и всех папок</li>
					<li>
						Два клика — случайное нажатие не снесёт трек
					</li>
					<li>
						<strong>Отменить нельзя</strong>
					</li>
					<li>
						Иконка <strong>ⓘ</strong> рядом с кнопкой — подробности
					</li>
				</ul>
			),
			nextLabel: 'Понятно →',
		},
	]
}

/**
 * Storage key для тура «Работать с треком» (SunoActionsSheet).
 */
export const SUNO_ACTIONS_SHEET_TOUR_KEY = 'umnik_suno_actions_sheet_tour_done'

// ── Suno Source Track Pin tours (per-operation) ────────────────

/**
 * Per-operation туры для SunoSourceTrackPin — запускаются когда юзер
 * в первый раз заходит в конкретную параметрическую операцию (Продлить,
 * Cover, + Вокал, + Инструментал). У каждой операции свой ключ, чтобы
 * юзер увидел пояснение именно к той операции которую выбрал, и переход
 * между операциями показал следующий тур независимо.
 *
 * Общая структура каждого тура:
 *   1. Табы операций (как переключаться)
 *   2-3. Уникальная часть операции (waveform/cut для extend, diff/plan
 *        для cover/add_vocals, diff для add_instrumental)
 *   N. Кнопка «Создать» — где найти, что делать дальше
 */

export type SunoPinOperation = 'extend' | 'cover' | 'add_vocals' | 'add_instrumental'

const OPS_TABS_STEP: TourStep = {
	targetSelector: '.suno-source-pin__ops-tabs',
	title: 'Типы новых версий',
	description: (
		<ul className="spt__list">
			<li>
				<strong>Продлить</strong> — допишет продолжение к треку
			</li>
			<li>
				<strong>Cover</strong> — перепоёт в другом жанре
			</li>
			<li>
				<strong>+ Вокал</strong> — наложит голос на инструментал
			</li>
			<li>
				<strong>+ Инстр.</strong> — добавит музыку к вокалу
			</li>
			<li>Переключайся между ними — source-трек остаётся тот же</li>
		</ul>
	),
	nextLabel: 'Дальше →',
}

const SEND_STEP: TourStep = {
	targetSelector: '.gm__bottom .gm__send',
	mobileTargetSelector: '.gm__mobile-bar .gm__send, .gm__bottom .gm__send',
	title: 'Нажми «Создать»',
	description: (
		<ul className="spt__list">
			<li>«Создать» запустит генерацию через Suno — займёт 30–90 сек</li>
			<li>Получишь новую версию в той же карусели результата</li>
			<li>Если передумал — крестик в pin'е возвращает к обычной генерации</li>
		</ul>
	),
	nextLabel: 'Понятно →',
}

// Подсказки по полям виджета — какие пилюли и поля важны для каждой
// операции. Селектор `.suno-style-btn` = pill «Стиль» (SunoStyleButton),
// `.gm__pills` = вся строка пилюль (стиль, вокал, инструментал, кастомный),
// `.gm__prompt` = textarea с текстом/описанием.

const EXTEND_FIELDS_STEP: TourStep = {
	targetSelector: '.gm__pills',
	mobileTargetSelector: '.gm__mobile-model-btn, .gm__pills',
	title: 'Для продолжения — по желанию',
	description: (
		<ul className="spt__list">
			<li>
				Оставь всё как есть — Suno продолжит трек в том же стиле
			</li>
			<li>
				Или задай <strong>новый стиль</strong> — продолжение будет звучать иначе
			</li>
			<li>
				В поле <strong>«Опишите»</strong> можно добавить слова —
				Suno пропоёт их в продолжении
			</li>
		</ul>
	),
	nextLabel: 'Дальше →',
}

const COVER_STYLE_STEP: TourStep = {
	targetSelector: '.suno-style-btn',
	title: 'Обязательно измени «Стиль»',
	description: (
		<ul className="spt__list">
			<li>
				Нажми на пилюлю <strong>«Стиль»</strong> — откроется редактор жанров
			</li>
			<li>
				Укажи <strong>другой жанр</strong> чем у оригинала (иначе cover будет
				как оригинал)
			</li>
			<li>
				В пилюлях также: <strong>Инструментал</strong> (убрать вокал),{' '}
				<strong>Кастомный</strong> (твой vs auto-текст)
			</li>
			<li>
				Свой текст — в поле <strong>«Опишите»</strong> ниже
			</li>
		</ul>
	),
	nextLabel: 'Дальше →',
}

const ADD_VOCALS_FIELDS_STEP: TourStep = {
	targetSelector: '.gm__pills',
	mobileTargetSelector: '.gm__mobile-model-btn, .gm__pills',
	title: 'Пол голоса + стиль вокала',
	description: (
		<ul className="spt__list">
			<li>
				<strong>Вокал</strong> — выбери муж/жен/авто (какой голос наложить)
			</li>
			<li>
				<strong>Стиль</strong> — жанр вокала (опционально — под стиль
				инструментала)
			</li>
			<li>
				Слова для вокала — в поле <strong>«Опишите»</strong> ниже (или нажми
				«Сгенерировать текст песни» — ИИ напишет)
			</li>
		</ul>
	),
	nextLabel: 'Дальше →',
}

const ADD_INSTRUMENTAL_STYLE_STEP: TourStep = {
	targetSelector: '.suno-style-btn',
	title: 'Опиши инструментал в «Стиль»',
	description: (
		<ul className="spt__list">
			<li>
				Нажми на пилюлю <strong>«Стиль»</strong> — откроется редактор
			</li>
			<li>
				Опиши инструменты: <em>«acoustic guitar, soft piano, strings»</em>
			</li>
			<li>
				Без этого Suno не знает какой инструментал добавить к вокалу
			</li>
		</ul>
	),
	nextLabel: 'Дальше →',
}

function buildExtendPinTourSteps(): TourStep[] {
	return [
		OPS_TABS_STEP,
		{
			targetSelector: '.suno-source-pin__player',
			title: 'Плеер и точка обрезки',
			description: (
				<ul className="spt__list">
					<li>Слушай трек — нажми на обложку</li>
					<li>
						Двигай <strong>ползунок</strong> под волной или кликай прямо по
						волне — там появится <strong>жёлтая метка</strong>
					</li>
					<li>С этой секунды Suno начнёт дописывать продолжение</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-source-pin__legend',
			title: 'Что останется, что заменится',
			description: (
				<ul className="spt__list">
					<li>
						<strong>До метки</strong> — твой оригинал, ничего не изменится
					</li>
					<li>
						<strong>После метки</strong> — Suno допишет по стилю/тексту которые
						ты укажешь ниже
					</li>
					<li>Без метки — Suno допишет с самого конца трека</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		EXTEND_FIELDS_STEP,
		SEND_STEP,
	]
}

function buildCoverPinTourSteps(): TourStep[] {
	return [
		OPS_TABS_STEP,
		{
			targetSelector: '.suno-source-pin__diff',
			title: 'Старый жанр → новый',
			description: (
				<ul className="spt__list">
					<li>Слева — жанр оригинала, справа — новый стиль</li>
					<li>
						Чтобы получилось действительно другое — меняй <strong>Стиль</strong>{' '}
						в пилюле ниже («Jazz» → «Metal»)
					</li>
					<li>Если стили одинаковые — покажем warning, cover будет как оригинал</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-source-pin__plan',
			title: 'Что будет с текстом песни',
			description: (
				<ul className="spt__list">
					<li>
						<strong>Оригинал</strong> — текст сохранится, сменится только стиль
					</li>
					<li>
						<strong>Твой текст</strong> — напиши свою лирику в «Опишите» ниже
					</li>
					<li>
						<strong>Auto</strong> — Suno напишет новый текст под жанр (если
						выключить «Кастомный»)
					</li>
					<li>
						<strong>Инструментал</strong> — включи пилюлю «Инструментал», вокал
						уйдёт
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		COVER_STYLE_STEP,
		SEND_STEP,
	]
}

function buildAddVocalsPinTourSteps(): TourStep[] {
	return [
		OPS_TABS_STEP,
		{
			targetSelector: '.suno-source-pin__diff',
			title: 'К инструменталу → добавим голос',
			description: (
				<ul className="spt__list">
					<li>Исходная музыка останется — ритм, мелодия, инструменты</li>
					<li>
						Сверху наложится <strong>новая вокальная партия</strong>
					</li>
					<li>
						Пол голоса выбираешь в пилюле <strong>Вокал</strong> ниже (муж /
						жен / авто)
					</li>
					<li>
						Если source — трек с вокалом, покажем ошибку: Suno add-vocals
						работает только с инструменталом
					</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-source-pin__plan',
			title: 'Что споёт новый голос',
			description: (
				<ul className="spt__list">
					<li>
						Напиши в поле <strong>«Опишите»</strong> ниже —{' '}
						<strong>именно эти слова</strong> пропоёт голос, строка за строкой
					</li>
					<li>Оставь пусто — Suno сам придумает текст под жанр</li>
					<li>Используй кнопку «Сгенерировать текст песни» — ИИ напишет лирику</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		ADD_VOCALS_FIELDS_STEP,
		SEND_STEP,
	]
}

function buildAddInstrumentalPinTourSteps(): TourStep[] {
	return [
		OPS_TABS_STEP,
		{
			targetSelector: '.suno-source-pin__diff',
			title: 'К вокалу → добавим инструменты',
			description: (
				<ul className="spt__list">
					<li>Вокал останется оригинальным — голос, слова, мелодия</li>
					<li>
						Снизу добавится новая <strong>инструментальная дорожка</strong>
					</li>
					<li>
						Опиши что хочешь услышать в пилюле <strong>Стиль</strong>:{' '}
						<em>«acoustic guitar, soft piano, strings»</em>
					</li>
					<li>Без стиля Suno не поймёт какой инструментал нужен</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		ADD_INSTRUMENTAL_STYLE_STEP,
		SEND_STEP,
	]
}

/**
 * Возвращает шаги тура для конкретной параметрической операции.
 * Переключение операций в pin'е → вызов с новым аргументом → новый тур.
 */
export function buildPinTourSteps(op: SunoPinOperation): TourStep[] {
	switch (op) {
		case 'extend':
			return buildExtendPinTourSteps()
		case 'cover':
			return buildCoverPinTourSteps()
		case 'add_vocals':
			return buildAddVocalsPinTourSteps()
		case 'add_instrumental':
			return buildAddInstrumentalPinTourSteps()
	}
}

/**
 * Storage keys для per-operation туров в pin'е. У каждой операции свой,
 * потому что тур содержит информацию уникальную для этой операции —
 * пройдя Extend-тур юзер не узнает как работает Cover и наоборот.
 */
export const SUNO_PIN_TOUR_KEYS: Record<SunoPinOperation, string> = {
	extend: 'umnik_suno_pin_extend_tour_done',
	cover: 'umnik_suno_pin_cover_tour_done',
	add_vocals: 'umnik_suno_pin_add_vocals_tour_done',
	add_instrumental: 'umnik_suno_pin_add_instrumental_tour_done',
}

/**
 * Тур редактора «Заменить фрагмент трека» (SunoReplaceSectionSheet).
 * Показывается при первом открытии, после загрузки waveform (ready).
 *
 * 3 шага: выделение → прослушивание → поля и запуск.
 */
export const SUNO_REPLACE_TOUR_KEY = 'umnik_suno_replace_tour_done'

export function buildReplaceSheetTourSteps(): TourStep[] {
	return [
		{
			targetSelector: '.suno-replace-sheet__player',
			title: 'Выдели фрагмент для замены',
			description: (
				<ul className="spt__list">
					<li>Тяни выделение за <strong>края</strong> или передвигай целиком</li>
					<li><strong>Светлая зона</strong> будет переписана, затемнённая останется как есть</li>
					<li>Лимит Suno: фрагмент <strong>6–60 секунд</strong> и не больше половины трека</li>
				</ul>
			),
			nextLabel: 'Дальше →',
		},
		{
			targetSelector: '.suno-replace-sheet__fields',
			title: 'Опиши новый фрагмент и запускай',
			description: (
				<ul className="spt__list">
					<li>Поля необязательны: пусто — Suno перепишет <strong>в стиле трека</strong></li>
					<li>Результат придёт <strong>новым треком</strong> в «Доработки» — оригинал не изменится</li>
					<li>Шит можно закрыть: статус виден на треке, по готовности придёт уведомление</li>
				</ul>
			),
			nextLabel: 'Понятно',
		},
	]
}
